import { callFirebaseFunction } from 'boot/firebase';
import { network } from 'boot/network';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { chain, get, omit } from 'lodash';
import DataDB from 'src/fb/data-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import AppUtils from 'src/utils/AppUtils';
import {
  clearSessionFreezePatch, freezeDataDocPatch, freezeSessionPatch,
  restoreDataDocPatch, restoreSessionPatch, selectSessionsToPause, selectSessionsToResume,
} from 'src/utils/sessionPauseTimers';

import GenericDB from './generic-db';

/** Converts a sessionPauseTimers.js patch's millis offsets to Firestore Timestamps for writing. */
function toFirestorePatch({ accumulate, offsets }) {
  const timestamps = Object.entries(offsets)
    .map(([field, ms]) => [field, ms == null ? null : Timestamp.fromMillis(ms)]);
  return { ...accumulate, ...Object.fromEntries(timestamps) };
}

export default class SessionsDB extends GenericDB {
  #session;

  constructor() {
    super('sessions');
  }

  /**
   * Set up a session object from selected programs ready to store
   */
  static prepareSession(sessionInfo) {
    const retval = {
      sessionName: null,
      scheduledTimestamp: null,
      client: null,
      clinic: null,
      createdBy: null,
      runTimestamp: null,
      finishTimestamp: null,
      runBy: null,
      status: 'preparing',
      currentProgram: null,
      ...sessionInfo,
    };
    // delete retval.id;
    return retval;
  }

  /**
   * Create a new session in the database
   * @param sessionInfo
   * @param {*} selectedPrograms
   */
  async createSession(sessionInfo, selectedPrograms) {
    const newSession = SessionsDB.prepareSession(sessionInfo);
    const id = newSession.id ? newSession.id : null;
    // Create a session from selected programs
    const session = await this.create(omit(newSession, 'sessionPrograms'), id);

    const sessionProgramDb = new SessionProgramsDB(session.id);

    await sessionProgramDb
      .addProgramsToSession(session.id, selectedPrograms);

    return session.id;
  } // createSession()

  /**
   * Used only for offline mode
   *
   * @param {Object} session
   * @param {string} userId
   * @param {string } userName
   * @return {Promise<void>}
   */
  // eslint-disable-next-line class-methods-use-this
  async addNewSessionActivity(session, userId, userName) {
    const startTime = Timestamp.now();
    const newActivityType = 'session';
    const sessionNoteTemplateId = (session.sessionNoteTemplate || null);

    const sessionActivitiesDb = new SessionActivitiesDB();
    return sessionActivitiesDb.createBySessionData(
      session,
      userId,
      userName,
      newActivityType,
      startTime,
      sessionNoteTemplateId,
      session.appointment,
    );
  }

  /**
   * Update session to reflect that it is ended
   * @param {*} session A session object containing at min. {id and originalSessionId}
   * @param {*} clinic Connected clinic
   * @param {*} duration
   * @param {*} status
   */
  async endSession(session, clinic, duration = 0, status = 'done', from = null) {
    const sessionObj = {
      id: session.id,
      currentProgram: null,
      finishTimestamp: serverTimestamp(),
      status,
      hasTimecodedData: true,
      duration,
      durationOffsetTimestamp: null,
    };

    // Update aggregated info on scheduled session to minimize reads on main page
    if (session.originalSessionId) {
      this.logScheduledSessionHistory(session.originalSessionId, sessionObj.id, sessionObj.duration);
    }

    await this.update(sessionObj);

    // Complete local copies of session activities
    const sessionActivitiesDb = new SessionActivitiesDB();
    const updatedSession = { ...session, ...sessionObj };
    await sessionActivitiesDb.finishBySession(updatedSession, 'session', true);
  }

  /**
   * @param {Object} session
   * @param {Object & { user: string, userName: string }} userToHandover
   * @param {number} duration
   * @return {Promise<void>}
   */
  async handoverSession(session, userToHandover, duration) {
    await this.set(
      {
        duration,
        durationOffsetTimestamp: null,
        durationIsPaused: true,
        runBy: userToHandover.user,
        runByName: userToHandover.userName,
        appointment: null,
        isSessionActivityLocked: false,
      },
      session.id,
    );

    const newActivityType = 'session';
    const sessionActivitiesDb = new SessionActivitiesDB();
    await sessionActivitiesDb.finishBySession(session, newActivityType, true);

    if (network.offlineMode) {
      await sessionActivitiesDb.createBySessionData(
        session,
        userToHandover.user,
        userToHandover.userName,
        newActivityType,
        Timestamp.now(),
        session.sessionNoteTemplate || null,
      );
    }
  }

  /**
   * @param {Object} session
   * @param {number} duration
   */
  async suspendSession(session, duration) {
    await this.set(
      {
        duration,
        durationOffsetTimestamp: null,
        durationIsPaused: true,
        runBy: null,
        runByName: null,
        appointment: null,
        isSessionActivityLocked: false,
      },
      session.id,
    );

    // Complete local copies of session activities
    const sessionActivitiesDb = new SessionActivitiesDB();
    await sessionActivitiesDb.finishBySession(session, 'session', true);
  }

  /**
   * @param {Object} session
   * @param {Object} user
   * @param {Object} appointment
   */
  async resumeSuspendedSession(session, user, appointment) {
    const continueDurationData = {};

    if (session.durationIsPaused) {
      continueDurationData.durationOffsetTimestamp = Timestamp.now();
      continueDurationData.durationIsPaused = true;
    }

    await this.set({
      appointment: appointment || null,
      runBy: user.userId,
      runByName: user.name,
      ...continueDurationData,
    }, session.id);

    if (network.offlineMode) {
      const sessionActivitiesDb = new SessionActivitiesDB();
      await sessionActivitiesDb.createBySessionData(
        session,
        user.userId,
        user.name,
        'session',
        Timestamp.now(),
        session.sessionNoteTemplate || null,
      );
    }
  }

  /**
   * A group pause has to reach the sessions whose components are unmounted, or nothing freezes
   * their timers and the pause is silently added to their data on the next mount. Freezes every
   * live timer on each of `groupSessions` other than the active one, scoped to sessions this user
   * runs. HR-4624
   *
   * Resolves once the writes are issued, not acknowledged: offline, an acknowledgement waits for
   * the reconnect, and a pass still waiting then would run after the resume that should undo it.
   *
   * @param {Array<Object>} groupSessions
   * @param {{ activeSessionId: string, userId: string, pausedAt: Timestamp,
   *   isSuperseded?: () => boolean }} params
   * @return {Promise<void>}
   */
  async pauseGroupSessionTimers(groupSessions, {
    activeSessionId, userId, pausedAt, isSuperseded = () => false,
  }) {
    const pausedAtMs = pausedAt.toMillis();
    const sessions = selectSessionsToPause(groupSessions, { activeSessionId, userId });

    await AppUtils.allSettled(sessions.map((session) => this.#freezeSessionTimers(session, pausedAtMs, isSuperseded)));
  } // pauseGroupSessionTimers()

  /** @private */
  async #freezeSessionTimers(session, pausedAtMs, isSuperseded) {
    const dataDb = new DataDB();
    const dataDocs = await dataDb.readAll([['client', '==', session.client], ['session', '==', session.id]]);
    if (isSuperseded()) {
      return;
    }

    // Data documents are frozen before the session's pause flag is written: a mounted timer
    // (e.g. a second device with this session open) reacts to `durationIsPaused` by emitting its
    // own accumulator update, and doing that first would let freezeDataDocPatch() read the marker
    // as unset and add the same elapsed time again. Firestore applies and commits one client's
    // writes in the order they were issued, so the order holds without awaiting each one.
    const writes = dataDocs.map((dataDoc) => {
      const dataPatch = freezeDataDocPatch(dataDoc, pausedAtMs);
      return dataPatch && dataDb.set(toFirestorePatch(dataPatch), dataDoc.id);
    });

    const sessionPatch = freezeSessionPatch(session, pausedAtMs);
    if (sessionPatch) {
      writes.push(this.set(toFirestorePatch(sessionPatch), session.id));
    }

    AppUtils.allSettled(writes);
  } // #freezeSessionTimers()

  /**
   * Undoes {@link pauseGroupSessionTimers}: rebases every timer this device froze to `resumedAt`,
   * the same rebase a mounted `sessionPaused` watcher does on resume. Data documents are restored
   * before the session's pause flag lifts, so a second device with the same session open does not
   * wake its own timers from a stale offset in between. The active session is swept too - it can
   * carry a freeze from a time when a different client was on screen, and only this pass can undo
   * that, since its unmounted components left no timer to restore itself. HR-4624
   *
   * @param {Array<Object>} groupSessions
   * @param {{ activeSessionId: string, userId: string, resumedAt: Timestamp,
   *   isSuperseded?: () => boolean }} params
   * @return {Promise<void>}
   */
  async resumeGroupSessionTimers(groupSessions, {
    activeSessionId, userId, resumedAt, isSuperseded = () => false,
  }) {
    const resumedAtMs = resumedAt.toMillis();
    const sessions = selectSessionsToResume(groupSessions, { userId });

    await AppUtils.allSettled(sessions.map((session) => (
      this.#thawSessionTimers(session, resumedAtMs, session.id === activeSessionId, isSuperseded)
    )));
  } // resumeGroupSessionTimers()

  /** @private */
  async #thawSessionTimers(session, resumedAtMs, isActiveSession, isSuperseded) {
    const dataDb = new DataDB();
    const dataDocs = await dataDb.readAll([['client', '==', session.client], ['session', '==', session.id]]);
    if (isSuperseded()) {
      return;
    }

    const writes = dataDocs.map((dataDoc) => {
      const dataPatch = restoreDataDocPatch(dataDoc, resumedAtMs);
      return dataPatch && dataDb.set(toFirestorePatch(dataPatch), dataDoc.id);
    });

    const sessionPatch = isActiveSession
      ? clearSessionFreezePatch(session)
      : restoreSessionPatch(session, resumedAtMs);
    if (sessionPatch) {
      writes.push(this.set(toFirestorePatch(sessionPatch), session.id));
    }

    AppUtils.allSettled(writes);
  } // #thawSessionTimers()

  async logScheduledSessionHistory(originalSessionId, sessionId, duration = 0) {
    const originalSession = await this.read(originalSessionId);
    if (originalSession) {
      const sessionHistory = chain(originalSession)
        .get('sessionHistory', [])
        .takeRight(20)
        .concat({
          sessionId,
          // weekday: sessionObj.finishTimestamp,
          // @todo Firebase does not support server side timestamps in arrays
          finishTimestamp: Timestamp.fromDate(new Date()),
          duration,
        })
        .value();
      this.update({ id: originalSession.id, sessionHistory });
    }
  }

  setCurrentProgram(sessionId, currentProgram) {
    return this.set({ currentProgram }, sessionId);
  }

  /**
   * Soft-delete a session, associated activities and data
   *
   * @param {string} sessionId
   */
  async deleteSession(sessionId) {
    this.#session = await this.read(sessionId);
    if (!this.#session) {
      return;
    }

    await this.#softDeleteSession();
  } // deleteSession()

  /** @private */
  async #softDeleteSession() {
    const sessionActivitiesDb = new SessionActivitiesDB();

    // Delete data (not applicable for Store packaged sessions)
    if (get(this.#session, 'client', null) !== null) {
      const dataDb = new DataDB();
      await dataDb.softDeleteBySession(this.#session);
    }

    await sessionActivitiesDb.softDeleteBySession(this.#session);

    await this.update({
      id: this.#session.id,
      isDeleted: true,
      deleteTimestamp: serverTimestamp(),
    });
  }

  async hardDeleteSession(sessionId) {
    const session = await this.read(sessionId);
    if (!session) {
      return;
    }

    const originalSessionId = get(session, 'originalSessionId');
    if (originalSessionId) {
      const originalSession = await this.read(originalSessionId);
      if (originalSession) {
        const sessionHistory = chain(originalSession)
          .get('sessionHistory', [])
          .reject({ sessionId })
          .value();
        this.update({ id: originalSession.id, sessionHistory });
      }
    }

    if (session.status !== 'scheduled') {
      // Delete files (the whole /session/{sessionId} "directory")
      await callFirebaseFunction('deleteStorageDirectory', { prefix: `sessions/${sessionId}/` });
    }

    // Delete sessionFiles
    await this.deleteSubcollection(sessionId, 'sessionFiles');

    // Delete sessionPrograms
    await this.deleteSubcollection(sessionId, 'sessionPrograms');

    await this.delete(sessionId);
  }
}
